/**
 * WHO-6316: copy for the AI assistant consent page and the connected
 * assistants section. Keys live in the `account-settings` Lokalise namespace;
 * the defaults are the English source text. No em dashes (copy test).
 */

type Copy = { labelKey: string; defaultLabel: string };

const copy = (key: string, defaultLabel: string): Copy => ({
  labelKey: `account-settings:agent-oauth-${key}`,
  defaultLabel,
});

export const AGENT_OAUTH_COPY = {
  pageTitle: copy("page-title", "Connect an AI assistant"),
  intro: copy(
    "intro",
    "{{client}} wants to use your Whoppah account. Check what it may do before you allow it."
  ),
  canHeading: copy("can-heading", "It will be able to"),
  cannotHeading: copy("cannot-heading", "It will never be able to"),
  cannot: copy(
    "cannot",
    "Pay, read or send your messages, see your payouts or bank details, or change your account settings."
  ),
  // WHO-6321: shown instead of `cannot` when the request includes checkout:pay.
  cannotWithPay: copy(
    "cannot-with-pay",
    "See your card details, pay without asking you first, read or send your messages, see your payouts or bank details, or change your account settings."
  ),
  // DPIA addendum Agent-Actions, planned measure 4: the assistant's own terms apply to what it receives.
  assistantPrivacy: copy(
    "assistant-privacy",
    "What {{client}} receives from Whoppah is handled under {{client}}'s own privacy terms, not Whoppah's."
  ),
  returnsTo: copy("returns-to", "After you decide you return to {{host}}."),
  lifetime: copy(
    "lifetime",
    "Access lasts until you disconnect it in your security settings, or after 90 days without use."
  ),
  allow: copy("allow", "Allow"),
  deny: copy("deny", "Don't allow"),
  notInPilot: copy(
    "not-in-pilot",
    "Connecting AI assistants is in a small pilot and not open for your account yet."
  ),
  invalid: copy(
    "invalid",
    "This request from the assistant is not valid. Go back to the assistant and try again."
  ),
  signInAgain: copy(
    "sign-in-again",
    "For your safety, please sign in again before you connect an assistant."
  ),
  connectedTitle: copy("connected-title", "Connected AI assistants"),
  connectedIntro: copy(
    "connected-intro",
    "These assistants can act for you within what you allowed. Disconnect one and it stops working right away."
  ),
  lastUsed: copy("last-used", "Last used {{date}}"),
  neverUsed: copy("never-used", "Not used yet"),
  disconnect: copy("disconnect", "Disconnect"),
  disconnected: copy("disconnected", "{{client}} is disconnected"),
  error: copy("error", "Something went wrong. Please try again."),
} as const;

/** One sentence per scope the gateway can grant (gateway `scopes.ts`). */
export const AGENT_SCOPE_COPY: Record<string, Copy> = {
  "catalog:read": copy(
    "scope-catalog-read",
    "Search listings and see prices, details and shipping costs"
  ),
  "favorites:write": copy(
    "scope-favorites-write",
    "Add items to your favorites"
  ),
  "searches:write": copy(
    "scope-searches-write",
    "Save searches and alerts for you"
  ),
  "offers:write": copy(
    "scope-offers-write",
    "Make offers on items, after showing you the amount and asking you to confirm"
  ),
  "checkout:prepare": copy(
    "scope-checkout-prepare",
    "Put items in your cart and prepare checkout. You always pay yourself on whoppah.com"
  ),
  "checkout:pay": copy(
    "scope-checkout-pay",
    "Buy items for you with the card saved in the assistant, only after showing you the items, shipping and total and asking you to confirm. Whoppah buyer protection applies"
  ),
  "listings:draft": copy(
    "scope-listings-draft",
    "Start a draft listing for you. It is never published without you"
  ),
};
